import SearchIcon from '@mui/icons-material/Search';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import InputAdornment from '@mui/material/InputAdornment';
import { useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { startCase } from 'lodash-es';
import React, { useContext, useMemo, useState } from 'react';

import { LoadingSpinner } from '~components/Order/ordersDispatchStyledComponents';
import { Order } from '~hooks/useOrders/models';

import TableHeader from '../tickets-table/TableHeader';
import TicketSummaryQueryContext from '../tickets-table/TicketSummaryQueryContext/TicketSummaryQueryContext';
import EquipmentRow from './Rows/EquipmentRow';
import VendorRow from './Rows/VendorRow';
import { EquipmentData, VendorData } from './types';
import { columns, gridTemplateColumns } from './vendor-tickets-table-columns';

interface VendorTicketsSummaryProps {
  order: Order;
}

const VendorTicketsSummary = ({ order }: VendorTicketsSummaryProps) => {
  const theme = useTheme();
  const [expandedVendors, setExpandedVendors] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const orderUnitOfMeasure = startCase(order.unitOfMeasure?.name);

  const { data, isLoading } = useContext(TicketSummaryQueryContext);

  // Transform TicketsRollupSummary data into vendor structure
  const vendors = useMemo(() => {
    if (!data || data.length === 0) return [];

    // Group by external_vendor_name first
    const vendorGroups = data.reduce(
      (acc, ticketRollUp) => {
        const vendorName = ticketRollUp.externalVendorName || 'Unknown Vendor';

        if (!acc[vendorName]) {
          acc[vendorName] = {
            name: vendorName,
            ticketCount: 0,
            totalQuantity: 0,
            matchedTruckCount: 0,
            unmatchedTruckCount: 0,
            equipment: new Map<string, EquipmentData>(),
            unitOfMeasure: orderUnitOfMeasure,
          };
        }

        // Group by external_truck_id and isMatched status within each vendor
        const truckId = ticketRollUp.externalTruckId || 'Unknown Truck';

        // Create separate equipment entries for matched and unmatched tickets
        if (ticketRollUp.totalTicketsWithJobCount > 0) {
          const matchedKey = `${truckId}|true`;
          if (!acc[vendorName].equipment.has(matchedKey)) {
            acc[vendorName].equipment.set(matchedKey, {
              name: truckId,
              tickets: 0,
              totalQuantity: 0,
              isMatched: true,
            });
          }
          const matchedEquipment = acc[vendorName].equipment.get(matchedKey)!;
          matchedEquipment.tickets += ticketRollUp.totalTicketsWithJobCount;
          matchedEquipment.totalQuantity += ticketRollUp.totalTicketsWithJobQuantity;
        }

        if (ticketRollUp.totalTicketsMissingJobCount > 0) {
          const unmatchedKey = `${truckId}|false`;
          if (!acc[vendorName].equipment.has(unmatchedKey)) {
            acc[vendorName].equipment.set(unmatchedKey, {
              name: truckId,
              tickets: 0,
              totalQuantity: 0,
              isMatched: false,
            });
          }
          const unmatchedEquipment = acc[vendorName].equipment.get(unmatchedKey)!;
          unmatchedEquipment.tickets += ticketRollUp.totalTicketsMissingJobCount;
          unmatchedEquipment.totalQuantity += ticketRollUp.totalTicketsMissingJobQuantity;
        }

        // Update vendor totals
        acc[vendorName].ticketCount += ticketRollUp.totalTicketsCount;
        acc[vendorName].totalQuantity += ticketRollUp.totalTicketsQuantity;
        acc[vendorName].unitOfMeasure =
          ticketRollUp.unitOfMeasure || acc[vendorName].unitOfMeasure;

        return acc;
      },
      {} as Record<string, VendorData>,
    );

    // Calculate matched and unmatched truck counts for each vendor
    Object.values(vendorGroups).forEach((vendor) => {
      let matchedCount = 0;
      let unmatchedCount = 0;

      vendor.equipment.forEach((equipment) => {
        if (equipment.isMatched) {
          matchedCount++;
        } else {
          unmatchedCount++;
        }
      });

      vendor.matchedTruckCount = matchedCount;
      vendor.unmatchedTruckCount = unmatchedCount;
    });

    // Convert to VendorData format
    return Object.values(vendorGroups);
  }, [data]);

  const handleToggleExpanded = (vendorId: string) => {
    const newExpanded = new Set(expandedVendors);
    if (newExpanded.has(vendorId)) {
      newExpanded.delete(vendorId);
    } else {
      newExpanded.add(vendorId);
    }
    setExpandedVendors(newExpanded);
  };

  const filteredVendors = vendors.filter((vendor) =>
    vendor.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  if (isLoading) {
    return <LoadingSpinner isVisible={isLoading} />;
  }

  return (
    <Box sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 1 }}>
      <Box sx={{ px: 2, py: 1 }}>
        <Typography variant="h6">
          {t('dispatch.tickets_table.vendor_tickets_summary.title')}
        </Typography>
      </Box>
      <Divider />
      <TextField
        fullWidth
        placeholder="Filter by vendor name..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{ py: 1, px: 2 }}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          },
        }}
        data-ui={`page-dispatch__vendor-tickets-summary__order-${order.orderId}__input`}
      />
      <Box
        sx={{
          display: 'grid',
          gridAutoRows: 'auto',
          gridTemplateColumns,
          gridTemplateRows: '35px',
        }}
      >
        {columns.map((column) => (
          <TableHeader key={column.key} title={column.title} sx={column.sx} />
        ))}

        {filteredVendors.map((vendor) => (
          <React.Fragment key={vendor.name}>
            <VendorRow
              vendor={vendor}
              onToggleExpanded={handleToggleExpanded}
              isExpanded={expandedVendors.has(vendor.name)}
              columns={columns}
            />

            {expandedVendors.has(vendor.name) &&
              Array.from(vendor.equipment.values()).map((equipment) => (
                <EquipmentRow
                  key={`${equipment.name}|${equipment.isMatched}`}
                  equipment={equipment}
                  unitOfMeasure={vendor.unitOfMeasure}
                  columns={columns}
                />
              ))}
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
};

export default VendorTicketsSummary;
